export interface MusicSettings {
  readonly volume: number;
}

export const DEFAULT_MUSIC_SETTINGS: MusicSettings = {
  volume: 1,
};

export const MUSIC_TRACKS: readonly string[] = [
  '/audio/music/3 am West End.mp3',
  '/audio/music/Backbeat.mp3',
  '/audio/music/Goodnightmare.mp3',
  '/audio/music/Arpent.mp3',
  '/audio/music/Beat One.mp3',
  '/audio/music/Meditating Beat.mp3',
];

const clamp = (value: number, min: number, max: number): number => {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
};

const isFiniteNumber = (value: number): value is number => {
  return Number.isFinite(value);
};

export const normalizeMusicSettings = (
  settings: MusicSettings
): MusicSettings => {
  const volume = clamp(
    isFiniteNumber(settings.volume)
      ? settings.volume
      : DEFAULT_MUSIC_SETTINGS.volume,
    0,
    1
  );

  return {
    volume,
  };
};

const shuffle = <T>(values: readonly T[]): T[] => {
  const copy = values.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const current = copy[i];
    const candidate = copy[j];
    if (typeof current === 'undefined' || typeof candidate === 'undefined') {
      continue;
    }

    copy[i] = candidate;
    copy[j] = current;
  }
  return copy;
};

export default class MusicEngine {
  private readonly tracks: readonly string[];
  private readonly isSupported: boolean;
  private queue: string[];
  private audioElement: HTMLAudioElement | null = null;
  private currentTrack: string | null = null;
  private loadedTrack: string | null = null;
  private settings: MusicSettings;
  private waitingForInteraction: boolean = false;
  private suspended: boolean = false;
  private resumeAfterSuspend: boolean = false;
  private resumePosition: number | null = null;
  private readonly interactionUnlockHandler = (): void => {
    if (!this.waitingForInteraction) {
      return;
    }
    this.clearInteractionUnlock();
    this.ensurePlaying();
  };

  constructor(
    tracks: readonly string[] = MUSIC_TRACKS,
    settings: MusicSettings = DEFAULT_MUSIC_SETTINGS
  ) {
    this.tracks = tracks.length > 0 ? tracks : MUSIC_TRACKS;
    this.settings = normalizeMusicSettings(settings);
    this.isSupported =
      typeof window !== 'undefined' && typeof Audio !== 'undefined';
    this.queue = this.tracks.length > 0 ? shuffle(this.tracks) : [];
  }

  private getNextTrack(): string | null {
    if (this.tracks.length === 0) {
      return null;
    }

    if (this.queue.length === 0) {
      this.queue = shuffle(this.tracks);
    }

    return this.queue.shift() ?? null;
  }

  private getAudioElement(): HTMLAudioElement | null {
    if (!this.isSupported) {
      return null;
    }

    if (!this.audioElement) {
      const element = new Audio();
      element.preload = 'auto';
      element.loop = false;
      element.addEventListener('ended', () => {
        this.playNext();
      });
      element.addEventListener('error', () => {
        this.playNext();
      });
      this.audioElement = element;
      this.applyVolume();
    }

    return this.audioElement;
  }

  private setTrack(track: string): void {
    const element = this.getAudioElement();
    if (!element) {
      return;
    }

    element.src = track;
    this.loadedTrack = track;
  }

  private applyVolume(): void {
    const element = this.audioElement;
    if (!element) {
      return;
    }

    element.volume = this.settings.volume;

    if (this.settings.volume <= 0) {
      this.clearInteractionUnlock();
      if (!element.paused) {
        this.resumePosition = element.currentTime;
        element.pause();
      }
    }
  }

  private playTrack(
    track: string | null,
    resumePosition: number | null = null
  ): void {
    if (!track) {
      return;
    }

    const element = this.getAudioElement();
    if (!element) {
      return;
    }

    const shouldLoadTrack = this.loadedTrack !== track;
    if (shouldLoadTrack) {
      this.setTrack(track);
    }

    const effectiveResumePosition =
      typeof resumePosition === 'number' ? resumePosition : null;

    if (typeof effectiveResumePosition === 'number') {
      element.currentTime = effectiveResumePosition;
    } else if (shouldLoadTrack) {
      element.currentTime = 0;
    }

    this.applyVolume();

    if (this.settings.volume <= 0) {
      return;
    }

    this.resumePosition = effectiveResumePosition;
    void element
      .play()
      .then(() => {
        this.resumePosition = null;
        this.clearInteractionUnlock();
      })
      .catch(error => {
        const errorName = (error as { name?: string } | undefined)?.name;
        if (errorName === 'NotAllowedError') {
          this.requestInteractionUnlock();
          return;
        }
        // Other playback errors are ignored; the element will retry on the next track.
      });
  }

  public ensurePlaying(): void {
    if (!this.isSupported) {
      return;
    }

    if (this.suspended) {
      return;
    }

    if (!this.currentTrack) {
      this.currentTrack = this.getNextTrack();
    }

    if (!this.currentTrack) {
      return;
    }

    const resumePosition = this.resumePosition;
    this.playTrack(this.currentTrack, resumePosition);
  }

  public playNext(): void {
    if (!this.isSupported) {
      return;
    }

    if (this.suspended) {
      return;
    }

    this.resumePosition = null;
    this.currentTrack = this.getNextTrack();
    this.playTrack(this.currentTrack);
  }

  public stop(): void {
    if (!this.audioElement) {
      return;
    }

    this.audioElement.pause();
    this.audioElement.currentTime = 0;
    this.clearInteractionUnlock();
    this.resumePosition = null;
    this.loadedTrack = null;
  }

  public reset(): void {
    this.stop();
    this.queue = this.tracks.length > 0 ? shuffle(this.tracks) : [];
    this.currentTrack = null;
  }

  public updateSettings(settings: MusicSettings): void {
    this.settings = normalizeMusicSettings(settings);
    this.applyVolume();

    if (this.settings.volume > 0) {
      this.ensurePlaying();
    }
  }

  public setSuspended(suspended: boolean): void {
    if (!this.isSupported || this.suspended === suspended) {
      this.suspended = suspended;
      return;
    }

    this.suspended = suspended;

    const element = this.audioElement;
    if (!element) {
      this.resumeAfterSuspend = false;
      if (!suspended) {
        this.resumePosition = null;
      }
      if (!suspended && this.settings.volume > 0) {
        this.ensurePlaying();
      }
      return;
    }

    if (suspended) {
      this.resumeAfterSuspend = !element.paused && this.settings.volume > 0;
      if (!element.paused) {
        this.resumePosition = element.currentTime;
        element.pause();
      }
      return;
    }

    const shouldResume = this.resumeAfterSuspend && this.settings.volume > 0;
    this.resumeAfterSuspend = false;
    if (shouldResume) {
      if (this.currentTrack) {
        this.playTrack(this.currentTrack, this.resumePosition);
      } else {
        this.ensurePlaying();
      }
      return;
    }

    this.resumePosition = null;
    if (this.settings.volume > 0) {
      this.ensurePlaying();
    }
  }

  private requestInteractionUnlock(): void {
    if (!this.isSupported || typeof window === 'undefined') {
      return;
    }

    if (this.waitingForInteraction) {
      return;
    }

    this.waitingForInteraction = true;
    window.addEventListener('pointerdown', this.interactionUnlockHandler);
    window.addEventListener('keydown', this.interactionUnlockHandler);
    window.addEventListener('touchstart', this.interactionUnlockHandler);
  }

  private clearInteractionUnlock(): void {
    if (!this.waitingForInteraction || typeof window === 'undefined') {
      this.waitingForInteraction = false;
      return;
    }

    window.removeEventListener('pointerdown', this.interactionUnlockHandler);
    window.removeEventListener('keydown', this.interactionUnlockHandler);
    window.removeEventListener('touchstart', this.interactionUnlockHandler);
    this.waitingForInteraction = false;
  }
}
